import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSession } from '../lib/useSession'
import { LanguageMenu } from '../components/LanguageMenu'
import { Icon } from '../components/Icon'
import { useUiStore } from '../lib/uiStore'

const LOC_KEY = '7gcmail.locConsent'

type LocState = 'pending' | 'allowed' | 'skipped'

function readLocState(): LocState {
  try {
    const v = sessionStorage.getItem(LOC_KEY)
    if (v === 'allowed' || v === 'skipped') return v
  } catch {
    /* ignore */
  }
  return 'pending'
}

/** Map device locale → UI catalog code (one-shot; never store coordinates). */
function deviceLocaleCode(): string {
  const candidates = [...(navigator.languages || []), navigator.language || 'en']
  for (const raw of candidates) {
    const base = (raw || '').split('-')[0]?.toLowerCase()
    if (base) return base
  }
  return 'en'
}

export function WelcomePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useSession()
  const setLocale = useUiStore((s) => s.setLocale)
  const [locState, setLocState] = useState<LocState>(() => readLocState())
  const [locBusy, setLocBusy] = useState(false)

  useEffect(() => {
    // v2.1: default welcome language is English until user allows location or picks manually.
    if (locState === 'pending') {
      setLocale('en')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function persistLoc(next: LocState) {
    try {
      sessionStorage.setItem(LOC_KEY, next)
    } catch {
      /* ignore */
    }
    setLocState(next)
  }

  async function locAllow() {
    setLocBusy(true)
    try {
      // Request geolocation once for consent UX; language comes from device locale (no reverse-geo / no coords stored).
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        await new Promise<void>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => resolve(),
            () => resolve(),
            { maximumAge: 0, timeout: 8000, enableHighAccuracy: false },
          )
        })
      }
      setLocale(deviceLocaleCode())
      persistLoc('allowed')
    } finally {
      setLocBusy(false)
    }
  }

  function locSkip() {
    setLocale('en')
    persistLoc('skipped')
  }

  return (
    <section className="welcome card onboarding-v21">
      <div className="onb-top">
        <LanguageMenu />
        <button
          type="button"
          className="btn btn-ghost onb-loc"
          title={t('welcome.locationNote')}
          onClick={() => setLocState('pending')}
        >
          <Icon name="location_on" size={16} /> {t('welcome.locationShort')}
        </button>
      </div>
      <h1>{t('welcome.title')}</h1>
      <p className="lede">{t('welcome.lede')}</p>

      {locState === 'pending' ? (
        <div className="loc-card" role="dialog" aria-label={t('welcome.locTitle')}>
          <div className="loc-card-title">{t('welcome.locTitle')}</div>
          <p className="muted loc-card-note">{t('welcome.locBody')}</p>
          <div className="loc-card-actions">
            <button type="button" className="btn btn-primary" disabled={locBusy} onClick={() => void locAllow()}>
              {locBusy ? t('auth.working') : t('welcome.locAllow')}
            </button>
            <button type="button" className="btn btn-ghost" disabled={locBusy} onClick={locSkip}>
              {t('welcome.locSkip')}
            </button>
          </div>
        </div>
      ) : null}

      {isAuthenticated ? (
        <p className="ok">{t('welcome.signedInAs', { email: user?.email || '—' })}</p>
      ) : null}

      <div className="onb-providers">
        <button type="button" className="onb-card" onClick={() => navigate(isAuthenticated ? '/inbox?sec=add' : '/passkey')}>
          <span className="onb-ico" style={{ background: '#ea4335' }}>G</span>
          <b>Gmail</b>
          <span>{t('welcome.providerGmail')}</span>
        </button>
        <button type="button" className="onb-card" onClick={() => navigate(isAuthenticated ? '/inbox?sec=add' : '/passkey')}>
          <span className="onb-ico" style={{ background: '#0078d4' }}>O</span>
          <b>Microsoft</b>
          <span>{t('welcome.providerMs')}</span>
        </button>
        <button type="button" className="onb-card" onClick={() => navigate(isAuthenticated ? '/inbox?sec=add' : '/passkey')}>
          <span className="onb-ico" style={{ background: '#6001d2' }}>Y</span>
          <b>Yahoo</b>
          <span>{t('auth.yahooHint')}</span>
        </button>
        <button type="button" className="onb-card" onClick={() => navigate(isAuthenticated ? '/inbox?sec=add' : '/passkey')}>
          <span className="onb-ico" style={{ background: '#00796b' }}>@</span>
          <b>{t('auth.otherImap')}</b>
          <span>{t('welcome.providerImap')}</span>
        </button>
        <button type="button" className="onb-card" disabled title={t('mail.soon')}>
          <span className="onb-ico" style={{ background: 'var(--brand-purple)' }}>7</span>
          <b>@7-all.com</b>
          <span>{t('welcome.create7gc')} <span className="mail-ph">{t('mail.soon')}</span></span>
        </button>
      </div>

      <div className="welcome-actions">
        {isAuthenticated ? (
          <>
            <Link className="btn btn-primary" to="/inbox">
              {t('auth.gotoInbox')}
            </Link>
            <Link className="btn" to="/inbox?sec=add">
              {t('welcome.addAccount')}
            </Link>
            <Link className="btn btn-ghost" to="/sessions">
              {t('auth.sessions')}
            </Link>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/passkey')}>
              {t('welcome.continuePasskey')}
            </button>
            <button type="button" className="btn" onClick={() => navigate('/signup')}>
              {t('welcome.createAccount')}
            </button>
            <Link className="btn btn-ghost" to="/recovery">
              {t('auth.useRecovery')}
            </Link>
          </>
        )}
      </div>
      <p className="muted">{t('welcome.locationNote')}</p>
    </section>
  )
}
